import React, { useState } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import isEqual from 'lodash/isEqual'
import styled from 'styled-components'
import Icon from 'components/Icon'
import { _, colors } from 'utils'
import { RetrieveUserResponse } from 'types/swagger'
import { RootState } from '../../store'
import { saveProfileBadge, toggleProfile } from './editProfileSlice'
import { showErrorNotification } from '../notifications/notificationsSlice'

interface StyledProfileProps {
    isShow?: boolean;
    editingSocial?: Social | undefined;
    isSelecting?: boolean;
}
type Social = 'linkedIn' | 'facebook' | 'twitter' | 'instagram';

const S = {
    FormContent: styled.div`
        flex: 1;
    `,
    FormInput: styled(Form.Control)`
        height: 31px !important;
        margin-bottom: 10px;
        padding-bottom: 5px !important;
        resize: none;
        &::placeholder {
            font-style: italic;
            color: ${colors.grayTitleOpacity(0.5)};
        }
        &.profile-about {
            height: 90px !important;
        }
    `,
    Social: styled.div<StyledProfileProps>`
        display: flex;
        justify-content: space-between;
    `,
    SocialIcon: styled.div<StyledProfileProps>`
        display: ${({ isSelecting, editingSocial }) => (editingSocial && !isSelecting ? 'none' : 'flex')};
        transform: translateX(${({ isSelecting }) => (isSelecting ? '-100px' : 0)});
        margin-bottom: 12px;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        transition: 0.3s;
    `,
    SocialAction: styled.div<StyledProfileProps>`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 57px;
        left: 50px;
        width: 265px;
        transform: scale(${({ editingSocial }) => (editingSocial ? 1 : 0)});
        transform-origin: 0;
        opacity: ${({ editingSocial }) => (editingSocial ? 1 : 0)};
        transition: 0.3s;
    `,
    SocialInput: styled(Form.Control)`
        height: 31px !important;
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        &::placeholder {
            color: ${colors.gray3};
            font-style: italic;
        }
    `,
    SocialButton: styled.div<StyledProfileProps>`
        width: 60px;
        height: 30px;
        line-height: 30px;
        text-align: center;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
        background-color: ${colors.primary};
        color: white;
        cursor: pointer;
    `,
    Button: styled(Button)`
        display: flex;
        align-items: center;
        margin-top: 10px;
    `,
}

const checkEmptyString = (inputString = '') => !inputString.trim()
// eslint-disable-next-line
export const urlPattern = /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/
const checkValidSocials = (link) => {
    if (link) {
        return urlPattern.test(link)
    }
    return true
}

interface Props {
    profile: Partial<RetrieveUserResponse>
    callback: () => void
}

const EditProfileForm = ({ profile, callback }: Props) => {
    const dispatch = useDispatch()
    const editProfileStatus = useSelector((state: RootState) => state.editProfile.editProfileStatus)
    const isPreventHideProfile = useSelector((state: RootState) => state.editProfile.preventHideProfile)

    const [firstName, setFirstName] = useState(profile.first_name)
    const [lastName, setLastName] = useState(profile.last_name)
    const [company, setCompany] = useState(profile.company)
    const [jobTitle, setJobTitle] = useState(profile.jobtitle)
    const [about, setAbout] = useState(profile.about)
    const [editingSocial, setEditingSocial] = useState<Social | undefined>()
    const [socialData, setSocialData] = useState<Record<Social, string>>({
        facebook: profile.facebook,
        linkedIn: profile.linkedin,
        twitter: profile.twitter,
        instagram: profile.instagram,
    })

    const handleSaveSocialLink = () => {
        setEditingSocial(undefined)
    }

    const handleSubmitForm = () => {
        if (checkEmptyString(firstName) || checkEmptyString(lastName)) {
            dispatch(showErrorNotification(_('profile.firstname_lastname_required')))
            return
        }
        if (!checkValidSocials(socialData.linkedIn)
            || !checkValidSocials(socialData.facebook)
            || !checkValidSocials(socialData.twitter)
            || !checkValidSocials(socialData.instagram)
        ) {
            dispatch(showErrorNotification(_('profile.invalid_social_link')))
            return
        }
        setEditingSocial(undefined)
        const newProfile = {
            ...profile,
            id: profile.id,
            first_name: firstName,
            last_name: lastName,
            company,
            jobtitle: jobTitle,
            about,
            linkedin: socialData.linkedIn,
            facebook: socialData.facebook,
            twitter: socialData.twitter,
            instagram: socialData.instagram,
        }

        callback?.()
        // If don't change profile, but in case the account is new
        // and needs to be activated, still call API to save info and update isPreventHideProfile to false
        if (isEqual(profile, newProfile) && !isPreventHideProfile) {
            dispatch(toggleProfile())
            return
        }
        dispatch(saveProfileBadge(newProfile))
    }

    return (
        <S.FormContent>
            <S.FormInput
                type="text"
                size="sm"
                placeholder={_('profile.first_name').concat('*')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
            />

            <S.FormInput
                type="text"
                size="sm"
                placeholder={_('profile.last_name').concat('*')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
            />

            <S.FormInput
                type="text"
                size="sm"
                placeholder={_('profile.company_name')}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
            />

            <S.FormInput
                type="text"
                size="sm"
                placeholder={_('profile.job_title')}
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
            />

            <S.FormInput
                as="textarea"
                rows={3}
                placeholder={_('profile.about')}
                className="form-control form-control-sm mb-3 profile-about"
                value={about}
                onChange={(e) => setAbout(e.target.value)}
            />
            <S.Social>
                {Object.keys(socialData).map((social: Social) => (
                    <S.SocialIcon
                        editingSocial={editingSocial}
                        isSelecting={social === editingSocial}
                        key={social}
                        onClick={() => setEditingSocial(social)}
                    >
                        <Icon
                            icon={social}
                            color={socialData[social] ? colors.primary : colors.gray3}
                            style={{ marginBottom: 2 }}
                        />
                    </S.SocialIcon>
                ))}
                <S.SocialAction editingSocial={editingSocial}>
                    <S.SocialInput
                        type="text"
                        size="sm"
                        placeholder={editingSocial}
                        value={socialData[editingSocial]}
                        onChange={(e) => setSocialData({ ...socialData, [editingSocial]: e.target.value })}
                    />
                    <S.SocialButton editingSocial={editingSocial} onClick={handleSaveSocialLink}>
                        OK
                    </S.SocialButton>
                </S.SocialAction>
            </S.Social>
            <S.Button variant="primary" onClick={handleSubmitForm}>
                {_('profile.save_badge')}
                {
                    editProfileStatus === 'loading'
                        ? (
                            <Spinner
                                animation="border"
                                as="span"
                                size="sm"
                                style={{ color: 'white', marginLeft: 5 }}
                            />
                        )
                        : <Icon icon="check" color="white" style={{ margin: '0 3px 3px 0' }} size={20} />
                }

            </S.Button>
        </S.FormContent>
    )
}

export default EditProfileForm
